import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { InspirationEditButton } from "web_myplann/Inspiration/Components/InspirationEditButton";
import { InspirationSection } from "web_myplann/Inspiration/Components/InspirationSection";
import { InspirationValueRow } from "web_myplann/Inspiration/Components/InspirationValueRow";
import type { OwnHomeContext } from "web_myplann/Inspiration/OwnHome/Types/OwnHomeTypes";
import { formatInspirationMoney } from "web_myplann/Inspiration/Utils/inspirationMoney";

const R = Resources.Inspiration.OwnHome.Result;

type Props = {
	context: OwnHomeContext;
	hasEnteredValues: boolean;
	onEdit: () => void;
};

export const AffordabilitySection: FC<Props> = ({
	context,
	hasEnteredValues,
	onEdit,
}) => {
	const { t } = useResource();
	const theme = useTheme();

	const isFull = context.state === "full";
	const maxMortgage = context.maxMortgage ?? 0;
	const maxPayment = context.maxPayment ?? 0;
	const income = context.income ?? 0;
	const exactFootnote = hasEnteredValues
		? R.affordabilityFootnoteExact
		: R.affordabilityFootnoteBalance;
	const footnote = isFull ? exactFootnote : R.affordabilityFootnoteApprox;

	return (
		<InspirationSection
			title={t(R.affordabilityTitle)}
			rightAction={<InspirationEditButton label={t(R.edit)} onClick={onEdit} />}
		>
			<Stack
				bgcolor={theme.palette.surface.muted}
				borderRadius={theme.radii.lg}
				px={theme.spacingTokens.lg}
				py={theme.spacingTokens.md}
			>
				<Typography variant="button" color={theme.palette.grey[500]}>
					{isFull ? t(R.affordabilityExact) : t(R.affordabilityApprox)}
				</Typography>
				<Typography variant="h1" tabular mt={theme.spacingTokens.xs}>
					~{formatInspirationMoney(maxMortgage)}
				</Typography>
				<Typography variant="body2" mt={theme.spacingTokens.xxs}>
					{t(R.maxMortgageLabel)}
				</Typography>

				<Stack
					mt={theme.spacingTokens.md}
					pt={theme.spacingTokens.md}
					gap={theme.spacingTokens.sm}
					borderTop={theme.colors.border.solid(theme.palette.grey[200])}
				>
					<InspirationValueRow
						label={t(R.rowMaxPayment)}
						value={`~${formatInspirationMoney(maxPayment)}`}
					/>
					<InspirationValueRow
						label={t(R.rowIncome)}
						value={formatInspirationMoney(income)}
					/>
					{isFull && (
						<>
							<InspirationValueRow
								label={t(R.rowExpenses)}
								value={formatInspirationMoney(context.expenses ?? 0)}
							/>
							{context.cashflow !== undefined && (
								<InspirationValueRow
									label={t(R.rowCashflow)}
									value={formatInspirationMoney(context.cashflow)}
								/>
							)}
						</>
					)}
				</Stack>
			</Stack>

			<Typography
				variant="hint"
				color={theme.palette.grey[400]}
				mt={theme.spacingTokens.sm}
			>
				{t(footnote)}
			</Typography>
		</InspirationSection>
	);
};
